/**
 * #1330 / #1334 — Per-step Additional Information for the facility-power family.
 * Notes text stays on shared `powerAdditionalInformation`; attachments use per-step section ids.
 */

import { shouldShowPowerDistributionSection } from './facilityPowerScopeSections';
import { SECTION_IDS, type SectionId } from '../constants/sectionIds';

import type { SelectedFile } from '../components/forms/FileUpload';
import type { SavedAttachment } from '../services/attachmentService';

export type FacilityPowerFamilyStep =
  | 'facility-power'
  | 'large-ups'
  | 'power-distribution';

export function resolveFacilityPowerAdditionalAttachmentSectionId(
  step: FacilityPowerFamilyStep,
): SectionId {
  if (step === 'large-ups') {
    return SECTION_IDS.FACILITY_POWER_LARGE_UPS_ADDITIONAL;
  }
  if (step === 'power-distribution') {
    return SECTION_IDS.FACILITY_POWER_DISTRIBUTION_ADDITIONAL;
  }
  return SECTION_IDS.FACILITY_POWER_ADDITIONAL;
}

/**
 * Display attachments for Additional Information — includes legacy
 * `facility-power.additional` rows when a child step is scoped standalone (#8070).
 */
export function resolveFacilityPowerAdditionalAttachmentsForStep(
  step: FacilityPowerFamilyStep,
  attachmentsBySection:
    | Record<string, SavedAttachment[] | undefined>
    | null
    | undefined,
): SavedAttachment[] {
  const primary =
    attachmentsBySection?.[
      resolveFacilityPowerAdditionalAttachmentSectionId(step)
    ] ?? [];
  if (step === 'facility-power') {
    return primary;
  }
  const legacy =
    attachmentsBySection?.[SECTION_IDS.FACILITY_POWER_ADDITIONAL] ?? [];
  if (legacy.length === 0) {
    return primary;
  }
  const seen = new Set<number | string>();
  const merged: SavedAttachment[] = [];
  for (const attachment of [...primary, ...legacy]) {
    const key = attachment.id ?? attachment.attachmentName;
    if (key != null && seen.has(key)) {
      continue;
    }
    if (key != null) {
      seen.add(key);
    }
    merged.push(attachment);
  }
  return merged;
}

export function resolvePowerAdditionalInformationSummaryLabel(
  accordionKey: 'facilityPower' | 'largeUps' | 'powerDistribution',
): string {
  if (accordionKey === 'largeUps') {
    return 'Additional Large UPS Info';
  }
  if (accordionKey === 'powerDistribution') {
    return 'Additional Power Distribution Info';
  }
  return 'Additional Facility Power Info';
}

/**
 * Large UPS / Power Distribution unit editors — show Additional Information whenever
 * the child step is scoped (#1154 / #997), including when Facility Power is also scoped.
 */
export function shouldShowFacilityPowerAdditionalInformationInUnitEditor(options: {
  step: 'large-ups' | 'power-distribution';
  enabledStepIds: readonly string[] | null | undefined;
  stage?: string | null;
}): boolean {
  const enabledStepIds = Array.isArray(options.enabledStepIds)
    ? options.enabledStepIds
    : [];
  return shouldShowFacilityPowerAdditionalInformation({
    step: options.step,
    enabledStepIds,
    stage: options.stage,
  });
}

/** Repeating-section steps render Additional Information only inside the unit editor. */
export function shouldShowFacilityPowerAdditionalInformationAtStepLevel(_options: {
  step: 'large-ups' | 'power-distribution';
  enabledStepIds: readonly string[] | null | undefined;
  stage?: string | null;
}): boolean {
  return false;
}

export function shouldShowFacilityPowerAdditionalInformation(options: {
  step: FacilityPowerFamilyStep;
  enabledStepIds: readonly string[] | null | undefined;
  stage?: string | null;
}): boolean {
  const enabledStepIds = Array.isArray(options.enabledStepIds)
    ? options.enabledStepIds
    : [];
  if (options.step === 'facility-power') {
    return enabledStepIds.includes('facility-power');
  }
  if (options.step === 'large-ups') {
    return enabledStepIds.includes('large-ups');
  }
  if (options.step === 'power-distribution') {
    return (
      enabledStepIds.includes('power-distribution') &&
      shouldShowPowerDistributionSection({ stage: options.stage })
    );
  }
  return false;
}

/** Staged uploads for Additional Information — merges legacy section rows on child steps. */
export function resolveFacilityPowerAdditionalSelectedFilesForStep(
  step: FacilityPowerFamilyStep,
  selectedFiles: Record<string, SelectedFile[] | undefined> | null | undefined,
): SelectedFile[] {
  const primarySectionId =
    resolveFacilityPowerAdditionalAttachmentSectionId(step);
  const primary = selectedFiles?.[primarySectionId] ?? [];
  if (step === 'facility-power') {
    return primary;
  }
  const legacy = selectedFiles?.[SECTION_IDS.FACILITY_POWER_ADDITIONAL] ?? [];
  if (legacy.length === 0) {
    return primary;
  }
  if (primary.length === 0) {
    return legacy;
  }
  const seen = new Set<string>();
  const merged: SelectedFile[] = [];
  for (const file of [...primary, ...legacy]) {
    if (seen.has(file.id)) {
      continue;
    }
    seen.add(file.id);
    merged.push(file);
  }
  return merged;
}
